import * as ExpoCar from "$modules/expo-car";
import type { CarListItem, CarTab } from "$modules/expo-car";
import { stripUndefined } from "$src/core/services/car/car";
import buildCarCatalog, {
  isCarPlayable,
  toCarItem,
} from "$src/core/services/car/catalog/catalog";
import fetchCarCatalogData from "$src/core/services/car/catalog/catalog.data";
import type { CarCatalogData } from "$src/core/services/car/catalog/catalog.data";
import { pausePhonePlayer } from "$src/core/services/car/playback/playback.phone";
import resolveCarPlayback from "$src/core/services/car/playback/playback.resolve";
import { getCarRuntime } from "$src/core/services/car/runtime/runtime";
import type { CarItem } from "$src/core/services/car/types";
import Logging from "$src/core/services/logging/logging";

/** The CarPlay service task. Must not touch the React tree (a cold CarPlay-only launch never mounts it); `api`/`translate` come from the `./runtime` seam. */

/** SF Symbol for a section's CarPlay tab. */
const tabSymbol = (sectionId: string, title: string): string => {
  const id = sectionId.toLowerCase();
  if (id.includes("continue")) return "play.circle";
  if (id.includes("watchlist")) return "bookmark";

  const t = title.toLowerCase();
  if (t.includes("movie") || t.includes("film")) return "film";
  if (t.includes("serie") || t.includes("show")) return "tv";
  if (t.includes("episode") || t.includes("afleveringen")) return "play.tv";
  if (t.includes("popular") || t.includes("trending") || t.includes("populair"))
    return "flame";
  if (t.includes("brand") || t.includes("merk")) return "star";
  if (t.includes("live") || t.includes("channel") || t.includes("kanaal"))
    return "dot.radiowaves.left.and.right";
  return "rectangle.stack";
};

const playCarItem = async (item: CarItem): Promise<void> => {
  const runtime = getCarRuntime();
  if (!runtime) return;

  Logging.info("[car] tap", { id: item.id, type: item.type });
  try {
    const resolved = await resolveCarPlayback(runtime.api, {
      id: item.id,
      type: item.type,
    });
    Logging.info("[car] resolved", {
      id: item.id,
      playable: resolved !== null,
    });
    if (!resolved) {
      Logging.warn("CarPlay item has no playable sources", {
        id: item.id,
        type: item.type,
        title: item.title,
      });
      await ExpoCar.presentAlert(runtime.translate("ERROR/PLAYER_PAGE/TITLE"));
      return;
    }
    // A playing phone player fights the car player for audio and Now Playing.
    pausePhonePlayer();
    await ExpoCar.play(stripUndefined(resolved));
    // Not awaited: a failure here must not surface as a playback error.
    ExpoCar.showNowPlaying().catch((error: unknown) => {
      Logging.error("Could not show CarPlay Now Playing", { error });
    });
  } catch (error) {
    Logging.error("Could not resolve car playback", {
      error,
      id: item.id,
      type: item.type,
      title: item.title,
    });
    await ExpoCar.presentAlert(runtime.translate("ERROR/PLAYER_PAGE/TITLE"));
  }
};

const toListItem = (item: CarItem): CarListItem => ({
  title: item.title,
  ...(item.subtitle !== undefined && { subtitle: item.subtitle }),
  ...(item.imageUrl !== undefined && { iconUri: item.imageUrl }),
  onPress: () => {
    playCarItem(item).catch((error: unknown) => {
      Logging.error("Could not play car item", { error, id: item.id });
    });
  },
});

let playbackListenerRegistered = false;
const ensurePlaybackListener = (): void => {
  if (playbackListenerRegistered) return;
  playbackListenerRegistered = true;

  ExpoCar.addPlaybackEventListener(({ event, message }) => {
    if (event === "error") {
      Logging.error("[car] native playback error", { message });
      const runtime = getCarRuntime();
      if (runtime) {
        ExpoCar.presentAlert(
          runtime.translate("ERROR/PLAYER_PAGE/TITLE"),
        ).catch((error: unknown) => {
          Logging.error("Could not present car alert", { error });
        });
      }
      return;
    }
    Logging.debug(`[car] native playback ${event}`);
  });
};

let searchRegistered = false;
const ensureSearchListener = (): void => {
  if (searchRegistered) return;
  searchRegistered = true;

  ExpoCar.addSearchListener(({ query, requestId }) => {
    const runtime = getCarRuntime();
    if (!runtime) {
      ExpoCar.setSearchResults(requestId, []).catch((error: unknown) => {
        Logging.error("Could not set car search results", { error });
      });
      return;
    }
    runtime.api
      .getSearchResults({ query })
      .then((results) =>
        ExpoCar.setSearchResults(
          requestId,
          results.items.filter(isCarPlayable).map(toCarItem).map(toListItem),
        ),
      )
      .catch((error: unknown) => {
        Logging.error("Could not run car search", { error });
        return ExpoCar.setSearchResults(requestId, []);
      });
  });
};

let carTaskRunning: Promise<void> | null = null;
// A run arrived while one was in flight (fresher data): run once more after.
let rerunRequested = false;
// Warm-path data from the React tree; cold runs pass nothing and fetch.
let providedData: CarCatalogData | null = null;
// Unchanged data skips the re-root, which would yank the driver back to root.
let installedRootSnapshot: string | null = null;

// Disconnect clears the native template store: the next connect must re-root.
ExpoCar.addDisconnectListener(() => {
  installedRootSnapshot = null;
});

async function runCarTask(data?: CarCatalogData): Promise<void> {
  const runtime = getCarRuntime();
  if (!runtime) {
    Logging.warn("CarPlay task skipped: runtime not ready yet");
    return undefined;
  }
  if (data) providedData = data;
  if (carTaskRunning) {
    rerunRequested = true;
    return carTaskRunning;
  }

  carTaskRunning = (async () => {
    const { api, translate } = runtime;

    ensureSearchListener();
    ensurePlaybackListener();

    const provided = providedData;
    providedData = null;
    const { storefront, watchlist } =
      provided ?? (await fetchCarCatalogData(api, () => ExpoCar.isConnected()));

    const catalog = buildCarCatalog(storefront, watchlist, {
      continueWatching: translate("CAR/CONTINUE_WATCHING"),
      watchlist: translate("WATCHLIST/TITLE"),
    });

    const snapshot = JSON.stringify(catalog.sections);
    if (snapshot === installedRootSnapshot) return;

    const tabs: CarTab[] = catalog.sections.map((section) => ({
      title: section.title,
      symbol: tabSymbol(section.id, section.title),
      items: section.items.map(toListItem),
    }));

    if (tabs.length === 0) {
      tabs.push({ title: "HyView", items: [] });
    }

    // CarPlay cannot update a tab bar in place; re-root with a fresh tree.
    const rootTemplateId = await ExpoCar.createTemplate({
      type: "tabbar",
      tabs,
    });
    if (!rootTemplateId) {
      Logging.error("CarPlay createTemplate returned null; UI stays blank");
      return;
    }
    await ExpoCar.setRoot(rootTemplateId);
    installedRootSnapshot = snapshot;
  })()
    .catch((error: unknown) => {
      Logging.error("CarPlay task failed", { error });
    })
    .finally(() => {
      carTaskRunning = null;
      if (rerunRequested) {
        rerunRequested = false;
        runCarTask().catch((error: unknown) => {
          Logging.error("CarPlay task failed", { error });
        });
      }
    });

  return carTaskRunning;
}

export default runCarTask;
